import {
  computed,
  ref,
  toRaw,
} from 'vue';

import deepmerge from 'deepmerge';

import FORMATTERS from '@formatters/all';
import { depatternize } from '@formatters/patternize';
import monetize from '@formatters/monetize';

import useCallbacker from './callbacker';
import useCombiner from './combiner';
import usePropertier from './propertier';
import useValidator from './validator';
import useCloner from './cloner';

import filled from '../javascripts/filled';
import { identify, destroy } from '../javascripts/identified';
import fieldName, {
  combinedName,
  groupName,
  COMBINED_REGEXP,
} from '../javascripts/name';

import {
  VISIBLE_OPTIONS_TRUE,
  VISIBLE_OPTIONS_FALSE,
  VISIBLE_OPTIONS,

  READONLY_OPTIONS,
} from '../javascripts/options';

const PRIMARY_KEY = 'id';

export default function planter(props) {
  const _planter = {
    schema: ref({}),
    entity: computed(entityGetSet()),

    leaves: ref({}),
    parents: ref({}),
    branches: ref({}),
    errors: ref({}),
    loadings: ref({}),
    visibilities: ref({}),
    readonlies: ref({}),
    properties: ref({}),
    values: ref({}),

    _INITIALIZED: false,
  };

  const { callback } = useCallbacker(props, _planter);
  const { combine } = useCombiner(props, _planter);
  const { invalidate } = useValidator(props, _planter);
  const { clonable, cloning, clone } = useCloner(props, _planter);

  const {
    schema,
    entity,

    values,
    leaves,
    parents,
    branches,
    errors,
    // loadings,
    visibilities,
    readonlies,
    properties,
  } = _planter;

  const _values = ref({});
  function valuesGetSet(name) {
    return {
      get() {
        return _values.value[name];
      },
      set(value) {
        const file = properties.value[name].type === 'file';

        if (value?.blur || file) {
          combine(name);
          callback(name, value);
          invalidate(name);
        } else {
          const formatter = FORMATTERS[leaves.value[name].mask?.type];

          if (formatter) {
            let formatted = formatter(value, leaves.value[name].mask);

            if (leaves.value[name].mask?.type === 'monetize' && formatted === monetize(0, leaves.value[name].mask)) {
              formatted = '';
            }

            _values.value[name] = formatted;
          } else {
            _values.value[name] = filled(value) ? String(value) : '';
          }
        }
      },
    };
  }

  const _entity = ref({});
  function entityGetSet() {
    return {
      get() {
        return _entity.value;
      },
      set(value) {
        let clear = value._clear;
        delete value._clear;

        if (clear === true) {
          clear = Object.keys(values.value);
        } else if (clear) {
          clear = (Array.isArray(clear) ? clear : [clear])
            .map((i) => new RegExp(`^${i}\\[`));

          clear = Object
            .keys(values.value)
            .filter((name) => clear.some((i) => i.test(name)));
        }

        clear?.forEach((name) => {
          _values.value[name] = null;
        });

        _entity.value = props.normalizer(deepmerge(toRaw(value), {}), props);
        _replant();
      },
    };
  }

  let _brancher;
  function _plant(sch, ent, settings, identifier) {
    if (!identifier) {
      _brancher = null;
    }

    Object
      .keys(sch)
      .forEach((branch, index) => _branches(
        sch,
        ent,
        settings,
        identifier,
        branch,
        index,
      ));
  }

  function _replant() {
    _plant(schema.value, _entity.value, props.settings);
  }

  function _branches(sch, ent, settings, identifier, key, index) {
    const { group, fields } = sch[key];
    const name = fieldName(identifier, key);

    if (props.ungroup[identifier] || props.ungroup[name]) {
      // do nothing
    } else if (group) {
      _brancher = groupName(group);
    } else if (fields) {
      _brancher = groupName(name);
    }

    if (fields) {
      _leaves(sch, ent, settings, identifier, key);
    } else {
      _leaf(sch, ent, settings, identifier, key, index);
    }
  }

  function _leaves(sch, ent, settings, identifier, key) {
    const { fields } = sch[key];
    const array = Array.isArray(fields);

    (array ? fields : [fields])
      .forEach((field, index) => _plant(
        field,
        array ? ent?.[key]?.[index] : ent?.[key],
        array ? settings?.[key]?.[index] : settings?.[key],
        fieldName(identifier, key, array ? index : null),
      ));
  }

  function _leaf(sch, ent, settings, identifier, key) {
    const identified = ent?.[PRIMARY_KEY] && !sch[PRIMARY_KEY];
    const combined = props.combine[identifier];
    const combining = combined && !leaves.value[fieldName(identifier, combinedName(combined))];

    if (identified) {
      _harvest(sch, ent, settings, identifier, PRIMARY_KEY, ent.id, identify(sch, identifier, key));
      _harvest(sch, ent, settings, identifier, '_destroy', false, destroy(sch, identifier, key));
    }

    if (key) {
      _harvest(sch, ent, settings, identifier, key, ent?.[key], sch[key]);
    }

    if (combining) {
      let combo = combined.combine.map((i) => ent?.[i] || '');
      combo = combined.disparser ? combined.disparser(combo) : combo.join('');

      _harvest(
        sch,
        ent,
        settings,
        identifier,
        combinedName(combined),
        combo,
        {
          label: sch[key].label,
          description: sch[key].description,
          rules: sch[key].rules,

          ...combined,
        },
      );
    }
  }

  function _harvest(sch, ent, settings, identifier, key, value, leaf) {
    const name = fieldName(identifier, key);

    if (!sch[key]) {
      sch[key] = leaf;
    }

    errors.value[name] = errors.value[name] || null;
    parents.value[name] = identifier;
    branches.value[name] = _brancher;

    if (!leaves.value[name]) {
      leaves.value[name] = leaf;
      properties.value[name] = usePropertier(props, _planter, name);
      values.value[name] = computed(valuesGetSet(name));

      let visibility = VISIBLE_OPTIONS_TRUE;

      if (filled(settings?.[key])) {
        visibility = settings?.[`_${key}`]?.visibility || settings?.[`_${key}_visibility`] || VISIBLE_OPTIONS_FALSE;

        values.value[name] = settings[key];
        values.value[name] = { blur: true };
      }

      visibilities.value[name] = VISIBLE_OPTIONS.includes(visibility)
        ? VISIBLE_OPTIONS_TRUE
        : VISIBLE_OPTIONS_FALSE;
    }

    const zero = (i) => leaves.value[name].mask?.type === 'monetize' && i === monetize(0, leaves.value[name].mask);

    let _value;

    if (filled(values.value[name]) && !zero(values.value[name])) {
      // 1. user-inputted value
      _value = values.value[name];
    } else if (filled(value) && !zero(value)) {
      // 2. existing entity value
      _value = value;
    } else if (!filled(value) && COMBINED_REGEXP.test(name)) {
      // 3.1. combined field values (existing entity)
      _value = props.combine[identifier].combine.map((i) => ent?.[i] || '');

      // 3.2. if no value is present on entity, fallback to settings
      if (!_value.some((i) => filled(i))) {
        _value = props.combine[identifier].combine.map((i) => settings?.[i] || '');
      }

      _value = props.combine[identifier].disparser
        ? props.combine[identifier].disparser(_value)
        : _value.join('');
    } else if (filled(properties.value[name].constant)) {
      // 4. schema-defined constant value
      _value = properties.value[name].constant;
    } else if (properties.value[name].options?.length === 1) {
      // 5. single option select
      _value = properties.value[name].options[0].value;
    } else {
      // 6. empty value
      _value = '';
    }

    values.value[name] = _value;
    readonlies.value[name] = READONLY_OPTIONS.includes(
      settings?.[`_${key}`]?.readonly || settings?.[`_${key}_readonly`] || false,
    );
  }

  const tree = computed(() => Object
    .keys(leaves.value)
    .reduce((reduced, name) => {
      const {
        group,
        label,
        description,
      } = leaves.value[name];

      const path = parents.value[name]
        .replace(/]/g, '')
        .split('[');

      const last = path.slice(0).pop();

      if (!reduced[branches.value[name]]) {
        if (last === depatternize(last)) {
          path.pop();
        } else if (group) {
          path.splice(0, path.length);
        }

        const overwrite = path.reduce((r, i) => {
          const where = (r.fields?.[i] || r[i]);
          const optional = where?.fields?.find?.((j) => j._include_optional);
          const found = Object.values(r.fields?.[i]?.fields?.[last] || {})[0];

          if (optional && found) {
            return {
              label: found.group?.title,
              description: found.group?.subtitle,
            };
          }

          return where;
        }, schema.value);

        reduced[branches.value[name]] = {
          settings: props.settings?.simulation?._branches?.[branches.value[name]] || {},
          name: branches.value[name],
          title: overwrite?.label || group?.title || label,
          subtitle: overwrite?.description || group?.subtitle || description,
          leaves: [],
        };
      }

      reduced[branches.value[name]].leaves.push(name);

      return reduced;
    }, {}));

  const _schema = deepmerge(props.initializer(), {});
  function _initialize(sch, ent, identifier) {
    Object.keys(sch)
      .filter((key) => sch[key].fields)
      .forEach((key) => {
        const { fields } = sch[key];
        const array = Array.isArray(fields);
        const _key = fieldName(identifier, key);

        if (props.clones[_key]) {
          cloning(sch, identifier, key);

          if (fields.length < ent?.[key]?.length) {
            clone(sch, identifier, key, ent[key].length - fields.length);
          }
        }

        (array ? fields : [fields])
          .forEach((field, index) => _initialize(
            field,
            array ? ent?.[key]?.[index] : ent?.[key],
            _key,
          ));
      });
  }

  _initialize(_schema, props.entity);

  schema.value = _schema;
  entity.value = props.entity;

  _planter.tree = tree;

  _planter._COMPOSABLES = {
    callback,
    combine,
    invalidate,
    clonable,
    clone,
  };

  _planter._REPLANT = _replant;
  _planter._INITIALIZED = true;

  return _planter;
}
